// Firebase Cloud Messaging (FCM) push notification registration service
import * as fcmModule from 'firebase/messaging';
import type { Messaging, GetTokenOptions, MessagePayload } from 'firebase/messaging';
import {
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  getDocs,
  collection,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { app, db, auth } from 'src/config/firebase';

const getMessaging = fcmModule.getMessaging;
const onMessage = fcmModule.onMessage;
const isSupported = fcmModule.isSupported;

let isSupportedPromise: Promise<boolean> | null = null;

/**
 * Checks whether Firebase Cloud Messaging is supported in the current browser environment.
 */
export async function isFCMSupported(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
  if (typeof isSupported !== 'function') return false;

  if (!isSupportedPromise) {
    isSupportedPromise = isSupported().catch(() => false);
  }
  return isSupportedPromise;
}

/**
 * Safely retrieves the Firebase Messaging instance only when supported by the browser,
 * preventing `messaging/unsupported-browser` unhandled rejections in Safari and unsupported environments.
 */
export async function getMessagingSafely(): Promise<Messaging | null> {
  try {
    const supported = await isFCMSupported();
    if (!supported) return null;
    return getMessaging(app);
  } catch (err) {
    console.warn('[FCM Push] Browser does not support Firebase Messaging:', err);
    return null;
  }
}

type GetTokenFn = (messaging: Messaging, options?: GetTokenOptions) => Promise<string>;
type DeleteTokenFn = (messaging: Messaging) => Promise<boolean>;

const getFcmToken: GetTokenFn = (fcmModule as unknown as { getToken: GetTokenFn }).getToken;
const deleteFcmToken: DeleteTokenFn = (fcmModule as unknown as { deleteToken: DeleteTokenFn })
  .deleteToken;

export interface PushRegistrationResult {
  success: boolean;
  status: 'GRANTED' | 'DENIED' | 'UNSUPPORTED' | 'FAILED';
  token?: string;
  subId?: string;
  error?: string;
}

export interface PushSubscriptionItem {
  id: string;
  fcm_token: string;
  ua: string;
  created_at: string;
  last_active_at: string;
}

/**
 * Generates a stable unique document ID from an FCM token
 */
export function getSubscriptionId(token: string): string {
  return token.slice(-32).replace(/[^a-zA-Z0-9]/g, '') || 'fcm_device';
}

/**
 * Registers browser service worker, requests FCM notification permissions,
 * and saves the FCM token to Firestore.
 */
export async function registerBrowserPush(
  userId: string,
  userRole: 'trainer' | 'client' = 'trainer',
): Promise<PushRegistrationResult> {
  if (!userId || userId.includes('@')) {
    return {
      success: false,
      status: 'FAILED',
      error: 'Invalid user ID',
    };
  }

  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('Notification' in window)
  ) {
    return {
      success: false,
      status: 'UNSUPPORTED',
      error: 'Web Push Notifications are not supported in this browser.',
    };
  }

  try {
    // 1. Request Notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return {
        success: false,
        status: 'DENIED',
        error: 'Notification permission was denied.',
      };
    }

    // 2. Register Firebase Messaging Service Worker
    const swUrl = `${window.location.origin}/firebase-messaging-sw.js`;
    const registration = await navigator.serviceWorker.register(swUrl, { scope: '/' });
    await navigator.serviceWorker.ready;

    // 3. Obtain FCM Token
    const messaging = await getMessagingSafely();
    if (!messaging) {
      return {
        success: false,
        status: 'UNSUPPORTED',
        error: 'Web Push Notifications are not supported in this browser.',
      };
    }
    const token = await getFcmToken(messaging, {
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      return {
        success: false,
        status: 'FAILED',
        error: 'Failed to retrieve FCM token from Firebase.',
      };
    }

    // eslint-disable-next-line no-console
    console.log(
      '%c[FCM Push] FCM Registration Token:%c ' + token,
      'background: #F59E0B; color: #000; font-weight: bold; padding: 2px 6px; border-radius: 4px;',
      'color: #00E676; font-weight: bold;',
    );

    const subId = getSubscriptionId(token);
    const nowIso = new Date().toISOString();
    const userCollection = userRole === 'trainer' ? 'trainer_app_users' : 'flutter_user';

    // Store current device token & sub ID in localStorage for matching
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('minipt_current_fcm_token', token);
        localStorage.setItem('minipt_current_fcm_sub_id', subId);
      } catch {
        // Ignored
      }
    }

    // 4. Save to User document (main FCMToken field + fcm_tokens array)
    const userDocRef = doc(db, userCollection, userId);
    try {
      await updateDoc(userDocRef, {
        FCMToken: token,
        fcm_tokens: arrayUnion(token),
        fcm_updated_at: nowIso,
      });
    } catch {
      await setDoc(
        userDocRef,
        {
          FCMToken: token,
          fcm_tokens: [token],
          fcm_updated_at: nowIso,
        },
        { merge: true },
      );
    }

    // 5. Save device info in subcollection for device management
    const subDocRef = doc(db, userCollection, userId, 'push_subscriptions', subId);
    await setDoc(
      subDocRef,
      {
        fcm_token: token,
        endpoint: token,
        ua: navigator.userAgent,
        created_at: nowIso,
        last_active_at: nowIso,
      },
      { merge: true },
    );

    return {
      success: true,
      status: 'GRANTED',
      token,
      subId,
    };
  } catch (error: unknown) {
    const errMessage = error instanceof Error ? error.message : 'Unknown registration error';
    return {
      success: false,
      status: 'FAILED',
      error: errMessage,
    };
  }
}

/**
 * Removes a specific device's push subscription from Firestore and local state.
 */
export async function removePushSubscription(
  userId: string,
  subId: string,
  fcmToken?: string,
  userRole: 'trainer' | 'client' = 'trainer',
): Promise<boolean> {
  if (!userId) return false;
  try {
    const userCollection = userRole === 'trainer' ? 'trainer_app_users' : 'flutter_user';
    const subDocRef = doc(db, userCollection, userId, 'push_subscriptions', subId);
    await deleteDoc(subDocRef).catch(() => {});

    if (fcmToken) {
      const userDocRef = doc(db, userCollection, userId);
      await updateDoc(userDocRef, {
        fcm_tokens: arrayRemove(fcmToken),
      }).catch(() => {});
    }

    // If removing the subscription of this current browser session, unregister locally too
    if (typeof window !== 'undefined') {
      const currentSubId = localStorage.getItem('minipt_current_fcm_sub_id');
      const currentToken = localStorage.getItem('minipt_current_fcm_token');
      if (currentSubId === subId || (fcmToken && currentToken === fcmToken)) {
        localStorage.removeItem('minipt_current_fcm_sub_id');
        localStorage.removeItem('minipt_current_fcm_token');
        try {
          const messaging = await getMessagingSafely();
          if (messaging) {
            await deleteFcmToken(messaging);
          }
        } catch (e) {
          console.warn('Error deleting local FCM token:', e);
        }
      }
    }
    return true;
  } catch (error) {
    console.error('Failed to remove push subscription:', error);
    return false;
  }
}

/**
 * Unsubscribes FCM push notifications and clears the token in Firestore.
 */
export async function unsubscribeBrowserPush(
  userId: string,
  userRole: 'trainer' | 'client' = 'trainer',
): Promise<boolean> {
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('minipt_current_fcm_sub_id');
      localStorage.removeItem('minipt_current_fcm_token');
    }

    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;

    const messaging = await getMessagingSafely();
    if (!messaging) return false;

    const swUrl = `${window.location.origin}/firebase-messaging-sw.js`;
    const registration = await navigator.serviceWorker.getRegistration(swUrl);

    if (registration) {
      try {
        const token = await getFcmToken(messaging, { serviceWorkerRegistration: registration });
        if (token) {
          await deleteFcmToken(messaging);
          const subId = getSubscriptionId(token);
          const userCollection = userRole === 'trainer' ? 'trainer_app_users' : 'flutter_user';

          // Clean up Firestore
          const userDocRef = doc(db, userCollection, userId);
          await updateDoc(userDocRef, {
            FCMToken: '',
            fcm_tokens: arrayRemove(token),
          }).catch(() => {});

          const subDocRef = doc(db, userCollection, userId, 'push_subscriptions', subId);
          await deleteDoc(subDocRef).catch(() => {});
        }
      } catch (e) {
        console.warn('Error deleting FCM token:', e);
      }
    }
    return true;
  } catch (error) {
    console.error('Failed to unsubscribe push notifications:', error);
    return false;
  }
}

/**
 * Retrieves list of active registered push devices for the user.
 */
export async function getUserPushSubscriptions(
  userId: string,
  userRole: 'trainer' | 'client' = 'trainer',
): Promise<PushSubscriptionItem[]> {
  if (!userId || userId.includes('@')) {
    return [];
  }
  if (!auth.currentUser || auth.currentUser.uid !== userId) {
    return [];
  }
  try {
    const userCollection = userRole === 'trainer' ? 'trainer_app_users' : 'flutter_user';
    const subsRef = collection(db, userCollection, userId, 'push_subscriptions');
    const snap = await getDocs(subsRef);

    const items: PushSubscriptionItem[] = [];
    snap.forEach((d) => {
      const data = d.data();
      items.push({
        id: d.id,
        fcm_token: data.fcm_token || data.endpoint || '',
        ua: data.ua || 'Web Browser',
        created_at: data.created_at || '',
        last_active_at: data.last_active_at || '',
      });
    });
    return items;
  } catch (error) {
    console.warn('Unable to fetch push subscriptions:', error);
    return [];
  }
}

/**
 * Retrieves the current FCM token for this browser session.
 */
export async function getCurrentFCMToken(): Promise<string | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  const stored = localStorage.getItem('minipt_current_fcm_token');
  if (stored) return stored;
  try {
    const messaging = await getMessagingSafely();
    if (!messaging) return null;

    const swUrl = `${window.location.origin}/firebase-messaging-sw.js`;
    const registration = await navigator.serviceWorker.getRegistration(swUrl);
    if (registration) {
      const token = await getFcmToken(messaging, { serviceWorkerRegistration: registration });
      if (token) {
        localStorage.setItem('minipt_current_fcm_token', token);
        localStorage.setItem('minipt_current_fcm_sub_id', getSubscriptionId(token));
        return token;
      }
    }
  } catch {
    // Ignored
  }
  return null;
}

/**
 * Retrieves and logs the current FCM token for this browser session.
 */
export async function printCurrentFCMToken(): Promise<string | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const token = await getCurrentFCMToken();
    if (token) {
      // eslint-disable-next-line no-console
      console.log(
        '%c[FCM Push] Current Browser FCM Token:%c\n' + token,
        'background: #F59E0B; color: #000; font-weight: bold; padding: 2px 6px; border-radius: 4px;',
        'color: #00E676; font-family: monospace; font-weight: bold;',
      );
      return token;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[FCM Push] Could not retrieve active FCM token:', e);
  }
  return null;
}

/**
 * Foreground message listener when the app tab is active
 */
export function onForegroundFCMMessage(callback: (payload: MessagePayload) => void): () => void {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return () => {};
  }
  let unsubscribe: (() => void) | null = null;
  let isCancelled = false;

  getMessagingSafely()
    .then((messaging) => {
      if (!messaging || isCancelled) return;
      try {
        unsubscribe = onMessage(messaging, callback);
      } catch (e) {
        console.warn('FCM foreground listener initialization failed:', e);
      }
    })
    .catch(() => {});

  return () => {
    isCancelled = true;
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

// Expose helper to browser console for easy testing: window.printFCMToken()
if (typeof window !== 'undefined') {
  (window as unknown as { printFCMToken: typeof printCurrentFCMToken }).printFCMToken =
    printCurrentFCMToken;
}
